import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const partColorMap = {
  blue: { bg: 'bg-blue-200', text: 'text-blue-800', border: 'border-blue-300', tooltip: 'bg-blue-700' },
  green: { bg: 'bg-green-200', text: 'text-green-800', border: 'border-green-300', tooltip: 'bg-green-700' },
  yellow: { bg: 'bg-yellow-200', text: 'text-yellow-800', border: 'border-yellow-300', tooltip: 'bg-yellow-700' },
  red: { bg: 'bg-red-200', text: 'text-red-800', border: 'border-red-300', tooltip: 'bg-red-700' },
  purple: { bg: 'bg-purple-200', text: 'text-purple-800', border: 'border-purple-300', tooltip: 'bg-purple-700' },
  amber: { bg: 'bg-amber-200', text: 'text-amber-800', border: 'border-amber-300', tooltip: 'bg-amber-700' },
  gray: { bg: 'bg-gray-200', text: 'text-gray-800', border: 'border-gray-300', tooltip: 'bg-gray-700' },
};

export default function FormulaBreakdown({ formula, parts = [] }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [pinnedIdx, setPinnedIdx] = useState(null);
  const containerRef = useRef(null);

  const activeIdx = pinnedIdx !== null ? pinnedIdx : hoveredIdx;

  function handleClick(idx) {
    setPinnedIdx((prev) => (prev === idx ? null : idx));
  }

  // Build the formula display by replacing each part.text in the formula with a span
  function renderFormula() {
    if (!parts.length) {
      return <span className="text-gray-300">{formula}</span>;
    }

    // Find positions of each part in the formula
    const segments = [];
    let remaining = formula;
    let offset = 0;

    // Sort parts by their position in the formula to handle them in order
    const sortedParts = parts
      .map((p, i) => ({ ...p, originalIdx: i, pos: formula.indexOf(p.text) }))
      .sort((a, b) => a.pos - b.pos);

    for (const part of sortedParts) {
      const idx = remaining.indexOf(part.text);
      if (idx === -1) continue;

      // Text before this part
      if (idx > 0) {
        segments.push({ type: 'plain', text: remaining.slice(0, idx) });
      }
      segments.push({ type: 'part', text: part.text, partIdx: part.originalIdx });
      remaining = remaining.slice(idx + part.text.length);
    }
    if (remaining) {
      segments.push({ type: 'plain', text: remaining });
    }

    return segments.map((seg, i) => {
      if (seg.type === 'plain') {
        return (
          <span key={i} className="text-gray-400">
            {seg.text}
          </span>
        );
      }

      const part = parts[seg.partIdx];
      const colors = partColorMap[part.color] || partColorMap.gray;
      const isActive = activeIdx === seg.partIdx;

      return (
        <span key={i} className="relative inline-block">
          <span
            className={`relative z-10 cursor-pointer rounded px-0.5 transition-all duration-150 ${
              isActive
                ? `${colors.bg} ${colors.text} font-semibold`
                : 'text-gray-200 hover:text-gray-100'
            }`}
            onMouseEnter={() => setHoveredIdx(seg.partIdx)}
            onMouseLeave={() => setHoveredIdx(null)}
            onClick={() => handleClick(seg.partIdx)}
          >
            {seg.text}
          </span>

          <AnimatePresence>
            {isActive && (
              <motion.div
                initial={{ opacity: 0, y: 6, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 6, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className={`absolute z-30 left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 rounded-lg shadow-lg text-white text-xs max-w-[220px] w-max ${colors.tooltip}`}
              >
                <p className="font-semibold mb-0.5">{part.label}</p>
                <p className="opacity-90 leading-snug">{part.description}</p>
                <div
                  className={`absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 ${colors.tooltip}`}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </span>
      );
    });
  }

  return (
    <div ref={containerRef} className="rounded-lg border border-gray-700 bg-gray-900 p-4 overflow-x-auto">
      <pre className="font-mono text-base whitespace-pre-wrap leading-relaxed">
        {renderFormula()}
      </pre>

      {/* Legend */}
      {parts.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-700 flex flex-wrap gap-3">
          {parts.map((part, idx) => {
            const colors = partColorMap[part.color] || partColorMap.gray;
            const isActive = activeIdx === idx;
            return (
              <button
                key={idx}
                className={`text-xs px-2 py-1 rounded border transition-all duration-150 ${
                  isActive
                    ? `${colors.bg} ${colors.text} ${colors.border} font-semibold`
                    : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => handleClick(idx)}
              >
                {part.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
